import { useState, useCallback, useRef } from 'react';
import Mailcheck from 'mailcheck';
import { UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';

// Common email domains including Italian providers
const CUSTOM_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.it',
  'outlook.com',
  'outlook.it',
  'yahoo.com',
  'yahoo.it',
  'libero.it',
  'virgilio.it',
  'alice.it',
  'tin.it',
  'tiscali.it',
  'fastwebnet.it',
  'aruba.it',
  'pec.it',
  'legalmail.it',
  'icloud.com',
  'live.com',
  'live.it',
  'protonmail.com',
  'proton.me',
];

const CUSTOM_TLDS = ['com', 'it', 'net', 'org', 'eu', 'io', 'co'];

export interface EmailSuggestion {
  full: string;
  domain: string;
}

export interface EmailValidationState {
  suggestion: EmailSuggestion | null;
  mxWarning: string | null;
  isCheckingMx: boolean;
  applySuggestion: () => void;
  dismissSuggestion: () => void;
  dismissMxWarning: () => void;
  runChecks: (email: string) => Promise<void>;
}

/**
 * Enhanced email format validation that catches common mistakes.
 * Returns an i18n translation key if invalid, null if valid.
 */
export const validateEmailFormat = (email: string): string | null => {
  const trimmed = email.trim();
  if (!trimmed) return null;

  if (/\s/.test(trimmed)) return 'email_contains_spaces';
  if ((trimmed.match(/@/g) || []).length > 1) return 'email_double_at';
  if (!trimmed.includes('@')) return 'enter_valid_email';

  const domain = trimmed.split('@')[1];
  if (!domain || !domain.includes('.')) return 'email_domain_incomplete';

  const tld = domain.split('.').pop() || '';
  if (tld.length < 2 || tld.length > 63) return 'email_invalid_tld';

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'enter_valid_email';

  return null;
};

/**
 * Checks if a domain has MX records using Google DNS-over-HTTPS.
 * Fails open: returns true on any error so the user is never blocked.
 */
const checkMxRecord = async (domain: string): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);

    const res = await fetch(`https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=MX`, {
      signal: controller.signal,
    });
    clearTimeout(timeout);

    const data = await res.json();
    return data.Status === 0 && Array.isArray(data.Answer) && data.Answer.length > 0;
  } catch {
    // Fail open: never block the user on network errors
    return true;
  }
};

/**
 * Custom hook for smart email validation with typo suggestions and MX checks.
 * All checks are non-blocking — they produce warnings/suggestions, never prevent form submission.
 */
export const useEmailSmartValidation = (
  setValue: UseFormSetValue<RestaurantOnboardingPayload>,
  trigger: UseFormTrigger<RestaurantOnboardingPayload>
): EmailValidationState => {
  const [suggestion, setSuggestion] = useState<EmailSuggestion | null>(null);
  const [mxWarning, setMxWarning] = useState<string | null>(null);
  const [isCheckingMx, setIsCheckingMx] = useState(false);
  const lastCheckedDomainRef = useRef<string>('');

  const applySuggestion = useCallback(() => {
    if (suggestion) {
      setValue('email', suggestion.full);
      trigger('email');
      setSuggestion(null);
      setMxWarning(null);
    }
  }, [suggestion, setValue, trigger]);

  const dismissSuggestion = useCallback(() => setSuggestion(null), []);
  const dismissMxWarning = useCallback(() => setMxWarning(null), []);

  /**
   * Run mailcheck + MX verification on blur.
   * Called from the email field's onBlur handler.
   */
  const runChecks = useCallback(
    async (email: string) => {
      const trimmed = email.trim();
      if (!trimmed || validateEmailFormat(trimmed)) return;

      // Run mailcheck (synchronous)
      Mailcheck.run({
        email: trimmed,
        domains: CUSTOM_DOMAINS,
        topLevelDomains: CUSTOM_TLDS,
        suggested: (s: { full: string; domain: string }) => {
          setSuggestion({ full: s.full, domain: s.domain });
        },
        empty: () => {
          setSuggestion(null);
        },
      });

      // Run MX check (async) — skip if same domain was already checked
      const domain = trimmed.split('@')[1];
      if (domain && domain !== lastCheckedDomainRef.current) {
        lastCheckedDomainRef.current = domain;
        setIsCheckingMx(true);
        const hasMx = await checkMxRecord(domain);
        setIsCheckingMx(false);

        if (!hasMx) {
          setMxWarning(domain);
        } else {
          setMxWarning(null);
        }
      }
    },
    []
  );

  return {
    suggestion,
    mxWarning,
    isCheckingMx,
    applySuggestion,
    dismissSuggestion,
    dismissMxWarning,
    runChecks,
  };
};
